/* GASSISA
=========================================================================================
* Descripción: Menú de acciones del módulo de devoluciones
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 11/07/2024 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 22/03/2026 | Lic. José Luis Aragón Cervantes  | Ajuste de comportamiento de botones
*      3       | 03/05/2026 | Antigravity                      | Adición de botón sincronizar
*      4       | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Eliminación en submenú de otras acciones
*      5       | 03/06/2026 | Lic. José Luis Aragón Cervantes  | Estados de acciones anti fallas
*      6       | 03/06/2026 | Lic. José Luis Aragón Cervantes  | Reimpresión de remisiones en otras acciones
*      7       | 03/06/2026 | Lic. José Luis Aragón Cervantes  | Retira sincronización de acciones
*      8       | 03/06/2026 | Lic. José Luis Aragón Cervantes  | Bloqueo de operación con caja cerrada
* ---------------------------------------------------------------------------------------
*/

import { IconButton, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VerifiedIcon from "@mui/icons-material/Verified";
import HomeIcon from '@mui/icons-material/Home';
import UndoIcon from '@mui/icons-material/Undo';
import SearchIcon from '@mui/icons-material/Search';

import { Fragment } from "react";
import { useTools } from "../../context/Tools/ToolsContext";
import { useVentas } from "../../context/Ventas/VentasContext";
import { useDoubleClickPrevention } from '../../hooks/useDoubleClickPrevention';
import { useNotifyVentasReset } from '../../hooks/useBrowserTabsSync';
import MnuDangerActionsMenu from "../utils/MnuDangerActionsMenu";

export default function MnuDevolucionesActions({ onFocusBuscar, onOpenReimpresion, cajaAbierta = true }) {
    const { handleInicializarVentanaVentas } = useVentas();
    const sendVentasResetToOtherTabs = useNotifyVentasReset();
    const { isProcessing, executeWithProtection } = useDoubleClickPrevention();
    const {
        handleClickRegresarMainDashboard,
        handleClickNuevaDevolucion,
        handleCancelarDevolucion,
        handleApicarDevolucion,
        handleEliminarPartidaDevolucion,
        listaDevolucionesSelectas,
        statusEnableFieldsDevolucion,
        partidaSeleccionada,
    } = useTools();

    const hasDevoluciones = listaDevolucionesSelectas && listaDevolucionesSelectas.length > 0;
    const capturaActiva = !statusEnableFieldsDevolucion && cajaAbierta;
    const puedeNueva = statusEnableFieldsDevolucion && cajaAbierta;
    const puedeBuscar = capturaActiva;
    const puedeAplicar = capturaActiva && hasDevoluciones;

    const handleEliminarPartida = () => {
        handleEliminarPartidaDevolucion(null, partidaSeleccionada?.id);
    };

    return (
        <Fragment>
            <Tooltip title={"Home"}>
                <IconButton onClick={handleClickRegresarMainDashboard}>
                    <HomeIcon className="icon" /><p className="icon"> Salir</p>
                </IconButton>
            </Tooltip>

            <Tooltip title={"Nueva devolución"}>
                <span>
                    <IconButton 
                        onClick={() => {
                            executeWithProtection(handleClickNuevaDevolucion);
                            if (onFocusBuscar) {
                                setTimeout(() => onFocusBuscar(), 200);
                            }
                        }} 
                        disabled={isProcessing || !puedeNueva}
                    >
                        <AddIcon className="icon" /><p className="icon">Nueva</p>
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title={puedeBuscar ? "Buscar (Alt+F1)" : "Primero presione Nueva"}>
                <span>
                    <IconButton 
                        onClick={() => onFocusBuscar && onFocusBuscar()}
                        disabled={isProcessing || !puedeBuscar}
                    >
                        <SearchIcon className="icon" /><p className="icon">Buscar</p>
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title={"Deshacer"}>
                <span>
                    <IconButton 
                        onClick={() => executeWithProtection(handleCancelarDevolucion)}
                        disabled={isProcessing || !capturaActiva}
                    >
                        <UndoIcon className="icon" /><p className="icon">Deshacer</p>
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title={"Aplicar"}>
                <span>
                    <IconButton 
                        onClick={() => executeWithProtection(async () => {
                            await handleApicarDevolucion(async () => {
                                await handleInicializarVentanaVentas();
                                sendVentasResetToOtherTabs({ applied: true });
                            });
                        })} 
                        disabled={isProcessing || !puedeAplicar}
                    >
                        <VerifiedIcon className="icon" /><p className="icon">Aplicar</p>
                    </IconButton>
                </span>
            </Tooltip>

            <MnuDangerActionsMenu
                tooltip="Otras acciones"
                actions={[
                    {
                        label: "Reimprimir remisión",
                        onClick: onOpenReimpresion,
                        disabled: false,
                    },
                    {
                        label: "Eliminar partida",
                        onClick: handleEliminarPartida,
                        disabled: statusEnableFieldsDevolucion || !hasDevoluciones || !partidaSeleccionada,
                    },
                ]}
            />
        </Fragment>
    );
}
